/**
 * Batch 18-D14 — Confidence Badge Display Mapping.
 *
 * Pure helper that maps a stored confidence level value into a
 * compact, operator-facing badge string. The result is stored on
 * `ScoredContextualArtifact.confidenceBadge` by the contextual
 * scorer and read by UI surfaces (e.g. `TrainingArtifactDetailModal`)
 * to label artifacts as "verified / community / provisional".
 *
 * Behavior is preserved BIT-FOR-BIT from
 * `src/utils/contextualTrainingScorer.ts` prior to Batch 18-D14:
 *
 *   - `'optibay_verified'`    → `'verified'`
 *   - `'community_validated'` → `'community'`
 *   - everything else (including `'provisional'`, `null`,
 *     `undefined`, empty string, and any string outside the
 *     `ConfidenceLevel` union)  → `'provisional'`
 *
 * The fallback is intentionally wide. The input type
 * (`string | null | undefined`) is defensively broader than the
 * canonical `ConfidenceLevel` union because the field can carry
 * partial-payload / stale-cache values at runtime even when the
 * canonical type says otherwise. Treating any such value as
 * `'provisional'` keeps the UI safely conservative.
 *
 * Separation of concerns:
 *   - This module owns the DISPLAY mapping (verified/community/
 *     provisional badge strings).
 *   - `./confidenceTier.ts` owns the SCORING multipliers
 *     (1.0 / 1.2 / 1.5) used by both contextual and engine scorers.
 *   - The two are deliberately split. A confidence tier value
 *     belongs to scoring arithmetic; a badge string belongs to UI
 *     rendering. Mixing them in one helper would conflate
 *     unrelated audit concerns (what numeric weight does this tier
 *     deserve vs what label does the operator see).
 *
 * Type:
 *   - `TrainingConfidenceBadge` is exported here as the canonical
 *     badge union. It is identical to the inline union the
 *     contextual scorer already uses on
 *     `ScoredContextualArtifact.confidenceBadge`
 *     (`'verified' | 'community' | 'provisional'`), so the scorer's
 *     call site assignment continues to compile via structural
 *     typing — no scorer-side change required.
 *
 * Out of scope:
 *   - The 18-D1 shared scaffold (`./types.ts`) does not include a
 *     badge re-export today. It MAY re-export
 *     `TrainingConfidenceBadge` in a future batch for one-stop
 *     import convenience; 18-D14 deliberately does not couple the
 *     scaffold to this module to keep this batch minimal.
 */

// ─────────────────────────────────────────────────────────────────────
// Badge union — operator-facing labels
// ─────────────────────────────────────────────────────────────────────

export type TrainingConfidenceBadge = 'verified' | 'community' | 'provisional';

// ─────────────────────────────────────────────────────────────────────
// Mapping helper
// ─────────────────────────────────────────────────────────────────────

/**
 * Get confidence badge from artifact confidence level
 */
export function getConfidenceBadge(
  confidenceLevel: string | null | undefined
): TrainingConfidenceBadge {
  switch (confidenceLevel) {
    case 'optibay_verified':
      return 'verified';
    case 'community_validated':
      return 'community';
    default:
      return 'provisional';
  }
}
